/**
 * Zone List Component
 *
 * Displays a table of all zones with their status and key metrics.
 * Includes status badges (healthy/degraded/error).
 * Rows are clickable to navigate to zone detail.
 */

import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { CheckCircle, AlertTriangle, XCircle } from 'lucide-react'
import type { ZoneCapacity, ZoneStatus } from '../../types'

/** Compute provider type */
const COMPUTE_TYPE = 'compute'

interface ZoneListProps {
    zones: ZoneCapacity[]
}

/** Get status badge props */
const getStatusBadge = (status: ZoneStatus) => {
    switch (status) {
        case 'healthy':
            return {
                variant: 'default' as const,
                className: 'bg-green-100 text-green-700 border-green-200',
                icon: CheckCircle,
                label: 'Healthy',
            }
        case 'degraded':
            return {
                variant: 'default' as const,
                className: 'bg-amber-100 text-amber-700 border-amber-200',
                icon: AlertTriangle,
                label: 'Degraded',
            }
        case 'error':
            return {
                variant: 'destructive' as const,
                className: 'bg-red-100 text-red-700 border-red-200',
                icon: XCircle,
                label: 'Error',
            }
        default:
            return {
                variant: 'secondary' as const,
                className: '',
                icon: AlertTriangle,
                label: status,
            }
    }
}

/** Get color class for usage percentage */
const getUsageClass = (pct: number): string => {
    if (pct >= 85) return 'text-red-600 font-semibold'
    if (pct >= 70) return 'text-amber-600'
    return 'text-green-600'
}

const ZoneList = ({ zones }: ZoneListProps) => {
    const navigate = useNavigate()

    // Filter to only show compute zones
    const computeZones = zones.filter((z) => z.provider_type === COMPUTE_TYPE)

    const handleRowClick = (zoneId: string) => {
        navigate(`/warehouse/cloud/zones/${zoneId}`)
    }

    if (computeZones.length === 0) {
        return (
            <Card className="mt-2">
                <CardHeader>
                    <CardTitle>Compute Zones</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        No compute zone data available
                    </div>
                </CardContent>
            </Card>
        )
    }

    return (
        <Card className="mt-2">
            <CardHeader>
                <CardTitle>Compute Zones ({computeZones.length})</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Zone</TableHead>
                                <TableHead>Provider</TableHead>
                                <TableHead className="text-right">
                                    Hosts
                                </TableHead>
                                <TableHead className="text-right">
                                    VMs
                                </TableHead>
                                <TableHead className="text-right">
                                    CPU
                                </TableHead>
                                <TableHead className="text-right">
                                    Memory
                                </TableHead>
                                <TableHead className="text-center">
                                    Status
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {computeZones.map((zone) => {
                                const statusBadge = getStatusBadge(zone.status)
                                const StatusIcon = statusBadge.icon
                                const hostCount =
                                    zone.compute.hosts?.length || 0
                                const vmCount =
                                    zone.compute.hosts?.reduce(
                                        (sum, h) => sum + h.vm_count,
                                        0
                                    ) || 0

                                return (
                                    <TableRow
                                        key={zone.zone_id}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() =>
                                            handleRowClick(zone.zone_id)
                                        }
                                    >
                                        <TableCell className="font-medium">
                                            {zone.zone_name}
                                        </TableCell>
                                        <TableCell>
                                            <span className="text-muted-foreground">
                                                {zone.provider_name}
                                            </span>
                                            <span className="text-xs text-muted-foreground/70 ml-1">
                                                ({zone.provider_type})
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {hostCount}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {vmCount}
                                        </TableCell>
                                        <TableCell
                                            className={`text-right ${getUsageClass(zone.compute.avg_cpu_usage_pct)}`}
                                        >
                                            {zone.compute.avg_cpu_usage_pct.toFixed(
                                                1
                                            )}
                                            %
                                        </TableCell>
                                        <TableCell
                                            className={`text-right ${getUsageClass(zone.compute.avg_memory_usage_pct)}`}
                                        >
                                            {zone.compute.avg_memory_usage_pct.toFixed(
                                                1
                                            )}
                                            %
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge
                                                variant={statusBadge.variant}
                                                className={`${statusBadge.className} gap-1`}
                                            >
                                                <StatusIcon className="h-3 w-3" />
                                                {statusBadge.label}
                                            </Badge>
                                            {zone.error && (
                                                <p className="text-xs text-red-500 mt-1">
                                                    {zone.error}
                                                </p>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    )
}

export default ZoneList
